import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";
import { Search, User } from "lucide-react";
import { useEffect, useState } from "react";
import { toast as sonnerToast } from "sonner";

interface Paciente {
  id: number;
  pessoa?: {
    nome: string;
    pessoaFisica?: {
      cpf: string;
    };
  };
  status: boolean;
}

interface ListaPacienteProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onSelectPatient: (patient: { id: number; name: string; cpf: string }) => void;
}

export const ListaPaciente = ({
  isOpen,
  onOpenChange,
  onSelectPatient,
}: ListaPacienteProps) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [pacientes, setPacientes] = useState<Paciente[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 10;

  // Buscar pacientes da API
  const buscarPacientes = async (pageNumber: number = 1, search: string = "") => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: pageNumber.toString(),
        limit: limit.toString(),
      });

      if (search.trim()) {
        params.append("search", search.trim());
      }

      const response = await api.get(`/api/paciente?${params.toString()}`);

      if (response?.result) {
        setPacientes(response.result.data || []);
        setTotalPages(response.result.pagination?.totalPages || 1);
        setTotal(response.result.pagination?.total || 0);
        setPage(pageNumber);
      }
    } catch (error: any) {
      console.error("Erro ao buscar pacientes:", error);
      sonnerToast.error("Erro ao buscar pacientes", {
        description: error.message || "Não foi possível carregar os pacientes",
      });
      setPacientes([]);
    } finally {
      setLoading(false);
    }
  };

  // Buscar quando o modal abre ou quando o termo de busca muda
  useEffect(() => {
    if (isOpen) {
      // Debounce para busca
      const timeoutId = setTimeout(() => {
        buscarPacientes(1, searchTerm);
      }, 500);

      return () => clearTimeout(timeoutId);
    } else {
      // Reset quando o modal fecha
      setSearchTerm("");
      setPage(1);
      setPacientes([]);
    }
  }, [isOpen, searchTerm]);

  const handleSelectPatient = (paciente: Paciente) => {
    const nome = paciente.pessoa?.nome || "";
    const cpf = paciente.pessoa?.pessoaFisica?.cpf || "";

    if (!nome) {
      sonnerToast.error("Erro", {
        description: "Paciente sem nome cadastrado",
      });
      return;
    }

    onSelectPatient({
      id: paciente.id,
      name: nome,
      cpf: cpf,
    });

    onOpenChange(false);
  };

  const formatCPF = (cpf: string) => {
    if (!cpf) return "";
    const cleaned = cpf.replace(/\D/g, "");
    return cleaned.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Selecionar Paciente</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="search">Buscar por nome ou CPF</Label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                id="search"
                type="text"
                placeholder="Digite o nome ou CPF do paciente..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>

          {loading ? (
            <div className="text-center py-8 text-muted-foreground">
              Carregando pacientes...
            </div>
          ) : pacientes.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <User className="h-12 w-12 mx-auto mb-2 opacity-50" />
              <p>
                {searchTerm
                  ? "Nenhum paciente encontrado"
                  : "Digite um nome ou CPF para buscar"}
              </p>
            </div>
          ) : (
            <>
              <div className="space-y-2 max-h-[400px] overflow-y-auto">
                {pacientes.map((paciente) => {
                  const nome = paciente.pessoa?.nome || "Sem nome";
                  const cpf = paciente.pessoa?.pessoaFisica?.cpf || "";

                  return (
                    <Card
                      key={paciente.id}
                      className="p-4 cursor-pointer hover:bg-secondary transition-colors"
                      onClick={() => handleSelectPatient(paciente)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                            <User className="h-5 w-5 text-primary" />
                          </div>
                          <div>
                            <p className="font-medium text-foreground">{nome}</p>
                            {cpf && (
                              <p className="text-sm text-muted-foreground">
                                CPF: {formatCPF(cpf)}
                              </p>
                            )}
                          </div>
                        </div>
                        <Button variant="outline" size="sm">
                          Selecionar
                        </Button>
                      </div>
                    </Card>
                  );
                })}
              </div>

              {/* Paginação */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between pt-4 border-t">
                  <div className="text-sm text-muted-foreground">
                    Mostrando {pacientes.length} de {total} pacientes
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => buscarPacientes(page - 1, searchTerm)}
                      disabled={page === 1 || loading}
                    >
                      Anterior
                    </Button>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-muted-foreground">
                        Página {page} de {totalPages}
                      </span>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => buscarPacientes(page + 1, searchTerm)}
                      disabled={page === totalPages || loading}
                    >
                      Próxima
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

